import * as SliderPrimitive from "@radix-ui/react-slider";
import {
  ComponentPropsWithoutRef,
  ElementRef,
  forwardRef,
  useEffect,
} from "react";
import { cn } from "@repo/ui/lib/utils";
import { useTranslations } from "next-intl";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
} from "@repo/ui/components/ui/form";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@repo/ui/components/ui/accordion";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAutoSubmit } from "@/hooks/useAutoSubmit";

type FilterMenuItemPriceProps = {
  className?: string;
  initialValue: [number, number];
  applyFilter?: (data: { minPrice: number; maxPrice: number }) => void;
} & ComponentPropsWithoutRef<typeof SliderPrimitive.Root>;

const FormPriceSchema = z.object({
  price: z.array(z.number()).refine((value) => value.some((item) => item), {}),
});

type PricePayload = z.infer<typeof FormPriceSchema>;

const FilterMenuItemPrice = forwardRef<
  ElementRef<typeof SliderPrimitive.Root>,
  FilterMenuItemPriceProps
>(({ className, initialValue, applyFilter, ...props }: any, ref) => {
  const t = useTranslations("Products-list.filters");
  const formatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  const form = useForm<z.infer<typeof FormPriceSchema>>({
    resolver: zodResolver(FormPriceSchema),
  });

  const { price } = form.watch();

  function handleApplyFilter(data: PricePayload) {
    const { price } = data;
    applyFilter({ minPrice: price[0], maxPrice: price[1] });
  }

  useAutoSubmit({
    trigger: form.trigger,
    watch: form.watch,
    onSubmit: form.handleSubmit(handleApplyFilter as any),
  });

  useEffect(() => {
    if (initialValue) {
      form.setValue("price", initialValue);
    }
  }, []);

  return (
    <Accordion type="single" collapsible>
      <AccordionItem value="item-1">
        <div className="py-[16px]">
          <AccordionTrigger className="max-md:text-base font-medium py-0 data-[state=open]:text-primary-magenta">
            {t("price")}
          </AccordionTrigger>
          <AccordionContent className="pb-0">
            <div className="flex flex-col justify-between p-6">
              <div>
                <div className="flex justify-end mb-6">
                  {price?.[0] !== undefined && price?.[1] !== undefined && (
                    <span className="block font-bold">
                      {formatter.format(price[0]).replace(",", ".")}€ -{" "}
                      {formatter.format(price[1]).replace(",", ".")}€
                    </span>
                  )}
                </div>
                <Form {...form}>
                  <form>
                    <FormField
                      control={form.control}
                      name="price"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <SliderPrimitive.Root
                              ref={ref}
                              value={form.getValues()?.price}
                              className={cn(
                                "relative flex w-full touch-none select-none items-center",
                                className,
                              )}
                              onValueChange={(val) =>
                                form.setValue("price", val)
                              }
                              onValueCommit={field.onChange}
                              {...props}
                            >
                              <SliderPrimitive.Track
                                data-vaul-no-drag
                                className="relative h-2 w-full grow overflow-hidden rounded-full bg-secondary-gray3"
                              >
                                <SliderPrimitive.Range className="absolute h-full bg-primary-magenta" />
                              </SliderPrimitive.Track>
                              <SliderPrimitive.Thumb
                                data-vaul-no-drag
                                className="block h-5 w-5 rounded-full  bg-primary-black dark:bg-primary-white ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                              />
                              <SliderPrimitive.Thumb
                                data-vaul-no-drag
                                className="block h-5 w-5 rounded-full  bg-primary-black dark:bg-primary-white ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                              />
                            </SliderPrimitive.Root>
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </form>
                </Form>
              </div>
            </div>
          </AccordionContent>
        </div>
      </AccordionItem>
    </Accordion>
  );
});
FilterMenuItemPrice.displayName = SliderPrimitive.Root.displayName;

export { FilterMenuItemPrice };
